/**
 * meeting-status-utils.ts — M-Rhythm-3 (CB-001)
 *
 * Shared status helper functions extracted from the four meeting components
 * that previously duplicated them:
 *   MeetingChip, RecurringRibbon, TodaysMeetingsStrip, MeetingDetailReadOnlyModal
 *
 * CB-001: Extract statusVariant() / statusLabel() to a shared file.
 */

import { t } from '@/lib/i18n';
import type { MeetingStatus } from '@/types/roros/meetings';

/** Map a MeetingStatus to a boilerplate Badge variant. */
export function statusVariant(
  status: MeetingStatus | string,
): 'success' | 'warning' | 'error' | 'default' | 'primary' {
  switch (status) {
    case 'completed':   return 'success';
    case 'in_progress': return 'primary';
    case 'paused':      return 'warning';
    case 'skipped':     return 'warning';
    case 'cancelled':   return 'error';
    default:            return 'default';
  }
}

/** Return a localised display label for a MeetingStatus. */
export function statusLabel(status: MeetingStatus | string): string {
  const map: Record<string, string> = {
    scheduled:   t('Scheduled'),
    in_progress: t('In progress'),
    paused:      t('Paused'),
    completed:   t('Completed'),
    skipped:     t('Skipped'),
    cancelled:   t('Cancelled'),
  };
  return map[status] ?? status;
}

/**
 * Client-side mirror of the server-side state machine.
 *
 * ⚠️  KEEP IN SYNC — SOURCE OF TRUTH IS THE SERVER
 * This function mirrors the ALLOWED map inside _validateTransition() at:
 *   server/modules/meetings/meetings.service.ts  (search: _validateTransition)
 *
 * If you add or remove a transition on the server you MUST update this
 * function in the same PR. The contract test in
 *   tests/unit/meeting-status-utils.test.ts
 * will fail if the two copies drift, acting as a merge-gate.
 *
 * Used by MeetingEditModal to filter the Status <Select> to valid transitions.
 *
 * Allowed transitions (F-Meeting-1 Wave 2 — must match server ALLOWED map exactly):
 *   scheduled   → in_progress | skipped | cancelled
 *   in_progress → completed   | paused  | skipped | cancelled
 *   paused      → in_progress | cancelled
 *   completed   → (terminal — no transitions)
 *   skipped     → (terminal — no transitions)
 *   cancelled   → (terminal — no transitions)
 *
 * Note: paused → completed is NOT allowed; resume first (server enforces this).
 */
export function getAllowedTransitions(from: MeetingStatus): MeetingStatus[] {
  switch (from) {
    case 'scheduled':   return ['in_progress', 'skipped', 'cancelled'];
    case 'in_progress': return ['completed', 'paused', 'skipped', 'cancelled'];
    case 'paused':      return ['in_progress', 'cancelled'];
    default:            return []; // terminal
  }
}

/** Returns true if the status is terminal (no further transitions). */
export function isTerminalStatus(status: MeetingStatus | string): boolean {
  return status === 'completed' || status === 'skipped' || status === 'cancelled';
}
